import { get, orderBy, uniqBy, round, startCase, toLower, isNil, isNaN } from 'lodash';
import { std } from 'mathjs';
import moment from 'moment';
import { KPI_GREEN, KPI_RED, KPI_YELLOW } from '../../../../core/chart/colors';

export const formatValueByIndicator = (indicator, value) => {
  if (isNil(value) || isNaN(value)) {
    return 'N/A';
  }

  const indicatorsFormattedAsDays = ['PARTNER_ACCESS_FREQUENCY'];
  const indicatorsFormattedAsHours = ['STUDENT_ENGAGEMENT'];
  const indicatorsFormattedAsAbsolute = ['STUDENT_STUDY_QUALITY', 'STUDENT_ACCESS_FREQUENCY'];
  const indicatorsFormattedAsPercent = ['PARTNER_LICENSE_PACKAGE_USAGE', 'PARTNER_STUDENT_VARIATION', 'STUDENT_CHURN'];
  const indicatorsFormattedWithPlus = ['PARTNER_STUDENT_VARIATION'];

  if (indicatorsFormattedAsDays.some((i) => i === indicator)) {
    return `Every ${value} days`;
  } else if (indicatorsFormattedAsHours.some((i) => i === indicator)) {
    return moment.duration(value, 'hours').format('*hh[h] mm[m]');
  } else if (indicatorsFormattedAsAbsolute.some((i) => i === indicator)) {
    return value;
  } else if (indicatorsFormattedAsPercent.some((i) => i === indicator)) {
    return `${indicatorsFormattedWithPlus.some((i) => i === indicator) && value > 0 ? '+' : ''}${value}%`;
  }
};

export const formatIndicator = (indicator) => startCase(toLower(indicator.replace('_', ' ').trim()));

export const getColorByIndicatorAndValue = (indicator, value) => {
  return {
    PARTNER_ACCESS_FREQUENCY: (value) => (value < 7 ? KPI_GREEN : value >= 14 ? KPI_RED : KPI_YELLOW),
    PARTNER_LICENSE_PACKAGE_USAGE: (value) => (value < 80 ? KPI_RED : value >= 90 ? KPI_GREEN : KPI_YELLOW),
    PARTNER_STUDENT_VARIATION: (value) => (value < -5 ? KPI_RED : value >= 0 ? KPI_GREEN : KPI_YELLOW),
    STUDENT_ACCESS_FREQUENCY: (value) => (value < 4 ? KPI_GREEN : value >= 8 ? KPI_RED : KPI_YELLOW),
    STUDENT_CHURN: (value) => (value < 5 ? KPI_GREEN : value >= 10 ? KPI_RED : KPI_YELLOW),
    STUDENT_ENGAGEMENT: (value) => (value < 3 ? KPI_RED : value >= 6 ? KPI_GREEN : KPI_YELLOW),
    STUDENT_STUDY_QUALITY: (value) => (value < 0 ? KPI_RED : value >= 5 ? KPI_GREEN : KPI_YELLOW),
  }[indicator](value);
};

export const getAverageByIndicator = (data, indicator, precision = 2) => {
  const total = data.reduce((sum, c) => sum + c.totalValue, 0);
  const amount = data.reduce((sum, c) => sum + c.registerAmount, 0);
  const indicatorValue = indicator === 'PARTNER_LICENSE_PACKAGE_USAGE' ? amount / total : total / amount;
  return round(
    indicatorValue *
      (['PARTNER_LICENSE_PACKAGE_USAGE', 'PARTNER_STUDENT_VARIATION', 'STUDENT_CHURN'].some((i) => i === indicator)
        ? 100
        : 1),
    precision
  );
};

export const groupValuesByKey = (currentArray, previousArray, groupBy, indicator) => {
  if (!currentArray.length && !previousArray.length) {
    return [];
  }

  const groupArray = uniqBy([...currentArray, ...previousArray], (x) => get(x, `${groupBy}.id`, null)).map((x) =>
    get(x, groupBy, { id: 'NA', name: 'N/A' })
  );

  return orderBy(
    groupArray.map((group) => {
      const currentForGroup = currentArray.filter((c) => group.id === get(c, `${groupBy}.id`, 'N/A'));
      const previousForGroup = previousArray.filter((c) => group.id === get(c, `${groupBy}.id`, 'N/A'));
      return {
        ...group,
        ...(currentForGroup.length
          ? {
              current: getAverageByIndicator(currentForGroup, indicator),
              currentAmount: currentForGroup.length,
              currentStandardDeviation: round(std(currentForGroup.map((c) => c.value)), 2),
            }
          : { current: null }),
        ...(previousForGroup.length
          ? {
              previous: getAverageByIndicator(previousForGroup, indicator),
              previousAmount: previousForGroup.length,
            }
          : { previous: null }),
      };
    }),
    [`${groupBy}.name`],
    ['asc']
  );
};

export const groupCompaniesByType = (currentArray, previousArray, type, typeId) => {
  if (!currentArray.length && !previousArray.length) {
    return [];
  }

  const groupArray = uniqBy([...currentArray, ...previousArray], (x) => x.company.id)
    .filter((item) => get(item, `${type}.id`) === typeId)
    .map((x) => x.company);

  return groupArray.map((group) => {
    const currentValue = get(
      currentArray.find((item) => item.company.id === group.id),
      'value',
      null
    );
    const currentAmount = get(
      currentArray.find((item) => item.company.id === group.id),
      'registerAmount'
    );
    const currentStandardDeviation = get(
      currentArray.find((item) => item.company.id === group.id),
      'standardDeviation'
    );
    const previousValue = get(
      previousArray.find((item) => item.company.id === group.id),
      'value',
      null
    );
    return {
      ...group,
      current: isNil(currentValue) ? currentValue : round(currentValue, 2),
      currentAmount: isNil(currentAmount) ? currentAmount : round(currentAmount, 2),
      currentStandardDeviation: isNil(currentStandardDeviation)
        ? currentStandardDeviation
        : round(currentStandardDeviation, 2),
      previous: isNil(previousValue) ? previousValue : round(previousValue, 2),
    };
  });
};
